import React, { useMemo, useState } from 'react';
import { useAuth } from '../../state/AuthContext';

const PROVIDER_CONFIG = {
  apple: {
    label: 'Sign in with Apple',
    ariaLabel: 'Sign in with Apple',
  },
  google: {
    label: 'Sign in with Google',
    ariaLabel: 'Sign in with Google',
  },
};
const ENABLE_APPLE_SIGN_IN = process.env.REACT_APP_ENABLE_APPLE_SIGN_IN === 'true';

const GoogleIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    className="h-5 w-5"
    viewBox="0 0 48 48"
  >
    <path
      fill="#EA4335"
      d={[
        'M24 9.5c3.54 0 6.73 1.23 9.24 3.65l6.9-6.9',
        'C35.94 2.05 30.28 0 24 0 14.62 0 6.51 5.38 2.56 13.22',
        'l8.04 6.24C12.2 12.42 17.62 9.5 24 9.5z',
      ].join(' ')}
    />
    <path
      fill="#4285F4"
      d={[
        'M46.98 24.55c0-1.64-.15-3.22-.43-4.75H24v9.02h12.95',
        'c-.56 3.02-2.25 5.58-4.79 7.3l7.7 5.98',
        'c4.5-4.15 7.12-10.26 7.12-17.55z',
      ].join(' ')}
    />
    <path
      fill="#FBBC05"
      d={[
        'M10.6 28.46c-.5-1.48-.78-3.06-.78-4.71s.28-3.23.78-4.71',
        'l-8.04-6.24C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.95',
        'l8.04-6.49z',
      ].join(' ')}
    />
    <path
      fill="#34A853"
      d={[
        'M24 48c6.48 0 11.93-2.13 15.9-5.8l-7.7-5.98',
        'c-2.13 1.43-4.86 2.27-8.2 2.27-6.38 0-11.8-2.92-13.4-7.18',
        'l-8.04 6.49C6.51 42.62 14.62 48 24 48z',
      ].join(' ')}
    />
  </svg>
);

const AppleIcon = () => (
  <span aria-hidden="true" className="text-lg leading-none">
    
  </span>
);

const normalizeProviders = (providers) => {
  if (!Array.isArray(providers) || providers.length === 0) {
    return ['google'];
  }
  return providers.filter((provider) => provider !== 'apple' || ENABLE_APPLE_SIGN_IN);
};

const AuthBubble = ({ promptText, providers, onSignIn, timestamp }) => {
  const { isAuthenticated } = useAuth();
  const [activeProvider, setActiveProvider] = useState(null);
  const [error, setError] = useState('');

  const resolvedProviders = useMemo(() => normalizeProviders(providers), [providers]);
  const isDisabled = Boolean(isAuthenticated || activeProvider);

  const handleSignIn = async (provider) => {
    if (!onSignIn || isDisabled) return;
    setActiveProvider(provider);
    setError('');
    try {
      await onSignIn(provider);
    } catch (signInError) {
      setError(signInError.message || 'Sign-in failed. Please try again.');
    } finally {
      setActiveProvider(null);
    }
  };

  return (
    <div
      className={
        'w-fit max-w-full rounded-2xl bg-gray-700 bg-opacity-70 px-4 py-2 ' +
        'text-gray-200 shadow-md'
      }
    >
      <div className="flex items-center space-x-2">
        <span className="text-sm">🔐</span>
        <p className="text-sm">
          {promptText || 'Sign in to continue the conversation'}
        </p>
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-red-600 bg-red-900/50 px-3 py-2">
          <p className="text-xs text-red-200">❌ {error}</p>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {resolvedProviders.map((provider) => {
          const config = PROVIDER_CONFIG[provider] || {};
          const isLoading = activeProvider === provider;
          const buttonLabel = config.label || 'Sign in';

          if (provider === 'apple') {
            return (
              <button
                key={provider}
                type="button"
                onClick={() => handleSignIn(provider)}
                disabled={isDisabled}
                aria-label={config.ariaLabel || buttonLabel}
                className={
                  'flex items-center justify-center gap-2 rounded-lg border border-black ' +
                  'bg-black px-4 py-2 text-sm font-medium text-white transition-colors ' +
                  'hover:bg-gray-900 focus-visible:outline-none focus-visible:ring-2 ' +
                  'focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60 ' +
                  'whitespace-nowrap'
                }
              >
                <AppleIcon />
                <span>
                  {isLoading ? 'Connecting...' : buttonLabel}
                </span>
              </button>
            );
          }

          if (provider === 'google') {
            return (
              <button
                key={provider}
                type="button"
                onClick={() => handleSignIn(provider)}
                disabled={isDisabled}
                aria-label={config.ariaLabel || buttonLabel}
                className={
                  'flex items-center justify-center gap-2 rounded-lg border border-gray-300 ' +
                  'bg-white px-4 py-2 text-sm font-medium text-gray-800 transition-colors ' +
                  'hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 ' +
                  'focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60 ' +
                  'whitespace-nowrap'
                }
              >
                <GoogleIcon />
                <span>
                  {isLoading ? 'Connecting...' : buttonLabel}
                </span>
              </button>
            );
          }

          return (
            <button
              key={provider}
              type="button"
              onClick={() => handleSignIn(provider)}
              disabled={isDisabled}
              aria-label={config.ariaLabel || buttonLabel}
              className={
                'flex items-center justify-center rounded-lg border border-gray-500 ' +
                'bg-gray-700 px-4 py-2 text-sm font-medium text-white transition-colors ' +
                'hover:bg-gray-600 focus-visible:outline-none focus-visible:ring-2 ' +
                'focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60 ' +
                'whitespace-nowrap'
              }
            >
              <span>{isLoading ? 'Connecting...' : buttonLabel}</span>
            </button>
          );
        })}
      </div>

      {timestamp && (
        <p className="mt-2 text-xs opacity-70">
          {new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      )}
    </div>
  );
};

export default AuthBubble;
